// [s381] What mag says she's doing while she does it. Spec 868e6180,
// section B.
//
// The chat surfaces stream `tool_call_started` events and show the tool
// name next to the thinking dots. Until this map, two partial copies of
// the same taxonomy covered 4 of the 27 tools reachable on the home
// surface, and everything else fell through to `${name}…` — so founders
// read "set_conversion_definition…", "veto_pending_action…",
// "notify_user…".
//
// §1 is not a style guide: any string mag says is a product
// requirement, and these are strings mag says. So the display
// vocabulary lives here, in schemas, where both the dashboard and the
// api can reach it. The tool DEFINITIONS can't move — they carry
// executors and live in apps/api — but their founder-facing names can,
// and a coverage test in apps/api asserts the two stay in step.
//
// Voice: present-continuous, lowercase, no exclamation, and what mag is
// DOING in the founder's terms rather than the system's. "checking your
// numbers", not "querying the analytics store". If a phrase can only be
// written in jargon, the phrase needs rewriting — not the tool
// excluding.
export const TOOL_PHRASES = Object.freeze({
    // ── analytics — reading what's happening on the site ──────────────
    events_search: 'searching your events…',
    events_timeseries: 'plotting the trend…',
    users_cohort: 'sizing the audience…',
    sessions_aggregate: 'rolling up sessions…',
    session_behavior: 'seeing how those visits went…',
    get_funnel: 'walking your funnel…',
    get_funnel_state: 'checking your funnel…',
    // ── memory — mag checking her own history before she claims anything ─
    search_brand_memory: 'checking what i told you before…',
    // The write side of the same faculty (52de0a2f gave chat the ability to
    // record a brand signal at all). Grouped with its read sibling rather
    // than with the campaign kit it actually ships in, because the founder
    // reads these as one thing: what mag knows about them.
    remember_brand_signal: 'noting that down…',
    // ── brand + campaign state — what mag already knows ───────────────
    get_brand_state: 'pulling up your brand…',
    read_brand_settings: 'checking your settings…',
    get_campaign_history: 'looking back at past campaigns…',
    get_campaign_rollup: 'adding up your campaigns…',
    request_campaign_scan: 'taking a fresh look…',
    // ── brand config — mag changing something the founder owns ────────
    update_brand_basics: 'updating your brief…',
    set_target_market: 'setting your target market…',
    set_conversion_definition: 'setting what counts as a win…',
    set_channel_settings: 'updating your channel setup…',
    set_notification_preferences: 'updating how i reach you…',
    // Ships in the autonomy kit, but it is the founder's own ceiling on
    // mag's spending rather than something she does on her own — so it
    // belongs with the settings they own.
    set_monthly_ad_budget: 'setting your monthly budget…',
    // ── campaigns — proposing and adjusting real spend ────────────────
    propose_campaign: 'drafting a campaign…',
    list_campaign_deliverables: "listing what's live…",
    pause_campaign_deliverable: 'pausing that one…',
    resume_campaign_deliverable: 'putting it back on…',
    adjust_campaign_budget: 'moving the budget…',
    // ── organic — posts and the assets behind them ────────────────────
    list_brand_assets: 'going through your assets…',
    draft_organic_post: 'writing a post…',
    publish_organic_post: 'putting the post up…',
    // ── autonomy — what mag did or is about to do on her own ──────────
    list_pending_autonomous_actions: "checking what i'm about to do…",
    veto_pending_action: 'calling that one off…',
    // ── what the founder's own ai tool has asked for ──────────────────
    // Mag's voice stays hers while making clear whose idea it was: she is
    // relaying someone else's request, not proposing one. "declining" rather
    // than anything about a mistake — she did not suggest it, so there is
    // nothing of hers being corrected.
    list_agent_requests: 'checking what your ai tool has asked for…',
    approve_agent_request: 'passing that through…',
    decline_agent_request: 'dropping that one…',
    // ── reaching the founder ──────────────────────────────────────────
    notify_user: 'making a note to tell you…',
});
/** Founder-facing phrase for a tool. Falls back to the raw name with an
 *  ellipsis — the pre-s381 behavior — so an unmapped tool degrades to
 *  ugly rather than to blank or to a crash. The coverage test in
 *  apps/api is what keeps the fallback unreachable in practice. */
export function toolPhrase(name) {
    return TOOL_PHRASES[name] ?? `${name}…`;
}
//# sourceMappingURL=tool-phrases.js.map